import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import "./Vision.css";
import logo from "../assets/logo.jpg";
import SEOHead from "./SEOHead";

const Vision = () => {
  const [activeTimeframe, setActiveTimeframe] = useState(0);
    const navigate = useNavigate();

  const visionElements = [
    {
      icon: '🌍',
      title: 'Global Community Network',
      description: 'A world where cultural boundaries don\'t limit your sense of belonging',
      detail: 'Every city becomes accessible, every cuisine discoverable, and every person finds their tribe — regardless of where they move or explore.'
    },
    {
      icon: '🏠',
      title: 'Housing That Understands You',
      description: 'Properties and roommates matched by values, culture, and lifestyle',
      detail: 'We envision a future where no one settles for "good enough" — every home is a cultural and lifestyle match, not just a rental.'
    },
    {
      icon: '🍽️',
      title: 'Food as Connection',
      description: 'A world where every cuisine is discoverable wherever you are',
      detail: 'Food connects people to their heritage and opens doors to new cultures. ComLinkr makes every neighbourhood feel like home through food discovery.'
    },
    {
      icon: '📰',
      title: 'Informed & Connected Communities',
      description: 'Local news, events, and social stories that keep you plugged in',
      detail: 'From city guides to community events — Tech meetups, Food festivals, Arts markets, Wellness classes — ComLinkr keeps you informed and involved wherever you are.'
    }
  ];

  const futureTimeline = [
    {
      period: '2025–2026',
      title: 'Build & Launch',
      icon: '🌱',
      goals: [
        'Launch Housing, Food, Community & Feed features',
        'Go live in 10+ major cities across 3 countries',
        'Establish cultural matching algorithms',
        'Reach 200K+ early users'
      ],
      description: 'Shipping our core 4-feature platform and building our first communities'
    },
    {
      period: '2027–2028',
      title: 'Scale & Deepen',
      icon: '🧠',
      goals: [
        'Expand to 50+ cities globally',
        'Advanced AI personalisation across all 4 features',
        'Partner with local businesses and event organisers',
        '1M+ successful community connections'
      ],
      description: 'Deepening the product experience and growing globally'
    },
    {
      period: '2029–2030',
      title: 'Ecosystem',
      icon: '🌐',
      goals: [
        'Full lifestyle ecosystem — housing to events to news',
        'Integrate with local services, transit, and cultural venues',
        'Global community network across all major cities'
      ],
      description: 'Becoming the all-in-one platform for community living worldwide'
    },
    {
      period: '2031+',
      title: 'Universal Belonging',
      icon: '✨',
      goals: [
        'Presence in every major city worldwide',
        'Instant community integration for every mover',
        'Cultural bridge technology for all backgrounds',
        'The global standard for belonging'
      ],
      description: 'A world where everyone belongs — from day one, anywhere'
    }
  ];

  const impactVision = [
    {
      category: 'Social Impact',
      icon: '💝',
      metrics: [
        'Reduce loneliness by 70% for relocators',
        'Increase cultural integration success by 90%',
        'Create 10M+ meaningful connections',
        'Bridge cultural gaps in 200+ cities'
      ]
    },
    {
      category: 'Economic Impact',
      icon: '💰',
      metrics: [
        'Support local businesses worldwide',
        'Create employment opportunities',
        'Reduce housing search costs by 60%',
        'Enable global workforce mobility'
      ]
    },
    {
      category: 'Cultural Impact',
      icon: '🎭',
      metrics: [
        'Preserve cultural traditions globally',
        'Facilitate cross-cultural understanding',
        'Support minority communities',
        'Celebrate diversity in every city'
      ]
    },
    {
      category: 'Technological Impact',
      icon: '🔬',
      metrics: [
        'Pioneer AI-driven compatibility matching',
        'Advance cultural understanding tech',
        'Develop community prediction models',
        'Create belonging measurement standards'
      ]
    }
  ];

  const visionPrinciples = [
    {
      title: 'Inclusivity First',
      icon: '🌈',
      description: 'Every culture, every background, every lifestyle deserves to find belonging.'
    },
    {
      title: 'Technology for Humanity',
      icon: '🤖',
      description: 'Advanced AI and technology serving human connection and understanding.'
    },
    {
      title: 'Local Empowerment',
      icon: '🏘️',
      description: 'Strengthening local communities while connecting them globally.'
    },
    {
      title: 'Sustainable Growth',
      icon: '🌱',
      description: 'Building for long-term impact that benefits communities and planet.'
    },
    {
      title: 'Cultural Preservation',
      icon: '🏛️',
      description: 'Celebrating and preserving cultural diversity in an interconnected world.'
    },
    {
      title: 'Universal Access',
      icon: '🔓',
      description: 'Making community and belonging accessible to everyone, everywhere.'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTimeframe(prev => (prev + 1) % futureTimeline.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [futureTimeline.length]);

  return (
    <div className="vision-container">
      <SEOHead
        title="Our Vision — ComLinkr | A World Where Everyone Belongs"
        description="ComLinkr's vision is a world where finding housing, food, and community feels effortless — matched to your culture, lifestyle, and values."
        canonical="https://www.comlinkr.com/vision"
      />
        <button className="vision-back-btn" onClick={() => navigate('/')}>← Back to Home</button>
      {/* Hero Section */}
      <section className="vision-hero">
        <div className="hero-background">
          <motion.div className="floating-vision-elements">
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="floating-vision-element"
                animate={{
                  y: [0, -25, 0],
                  x: [0, 12, 0],
                  rotate: [0, 180, 360],
                  scale: [1, 1.1, 1]
                }}
                transition={{
                  duration: 5 + i,
                  repeat: Infinity,
                  delay: i * 0.4
                }}
                style={{
                  left: `${5 + i * 12}%`,
                  top: `${15 + (i % 4) * 20}%`
                }}
              >
                {visionElements[i % 4]?.icon}
              </motion.div>
            ))}
          </motion.div>
        </div>

        <div className="container text-center">
          <motion.img 
            src={logo} 
            alt="ComLinkr Logo" 
            className="vision-logo"
            initial={{ scale: 0, rotate: 180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 1.5, type: "spring", bounce: 0.4 }}
          />
          
          <motion.h1 
            className="vision-title"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 1 }}
          >
            Our <span className="vision-highlight">Vision</span>
          </motion.h1>
          
          <motion.p 
            className="vision-subtitle"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8, duration: 1 }}
          >
            A world where everyone finds their perfect community
          </motion.p>
          
          <motion.div 
            className="vision-statement"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.1, duration: 1 }}
          >
            <div className="statement-card">
              <h3>Imagine a world where belonging knows no borders</h3>
              <p>
                We envision a future where moving to any city feels like coming home, where 
                cultural compatibility is seamlessly matched, and where community connection 
                happens instantly. ComLinkr will make every place feel like home.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Vision Elements */}
      <section className="vision-elements-section">
        <div className="container">
          <motion.h2 
            className="section-title text-center mb-5"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
          >
            Our Vision in Action
          </motion.h2>
          
          <div className="row">
            {visionElements.map((element, index) => (
              <div key={index} className="col-lg-6 mb-4">
                <motion.div 
                  className="vision-element-card"
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  whileHover={{ scale: 1.02, y: -8 }}
                >
                  <div className="element-icon">{element.icon}</div>
                  <div className="element-content">
                    <h4>{element.title}</h4>
                    <p className="element-description">{element.description}</p>
                    <p className="element-detail">{element.detail}</p>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Timeline */}
      <section className="timeline-section">
        <div className="container">
          <motion.h2 
            className="section-title text-center mb-5"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
          >
            Our Journey to the Future
          </motion.h2>
          
          <div className="timeline-showcase">
            <div className="row">
              <div className="col-lg-8">
                <div className="active-timeline-display">
                  <motion.div 
                    key={activeTimeframe}
                    className="timeline-spotlight"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <div className="timeline-period">{futureTimeline[activeTimeframe].period}</div>
                    <div className="timeline-icon">{futureTimeline[activeTimeframe].icon}</div>
                    <h3>{futureTimeline[activeTimeframe].title}</h3>
                    <p className="timeline-description">{futureTimeline[activeTimeframe].description}</p>
                    <div className="timeline-goals">
                      {futureTimeline[activeTimeframe].goals.map((goal, index) => (
                        <div key={index} className="goal-item">
                          <span className="goal-check">✓</span>
                          <span>{goal}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </div>
              
              <div className="col-lg-4">
                <div className="timeline-navigation">
                  {futureTimeline.map((timeline, index) => (
                    <motion.div 
                      key={index}
                      className={`timeline-nav-item ${activeTimeframe === index ? 'active' : ''}`}
                      onClick={() => setActiveTimeframe(index)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span className="timeline-nav-icon">{timeline.icon}</span>
                      <div className="timeline-nav-content">
                        <strong>{timeline.period}</strong>
                        <span>{timeline.title}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Vision */}
      <section className="impact-vision-section">
        <div className="container">
          <motion.h2 
            className="section-title text-center mb-5"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
          >
            The Impact We Envision
          </motion.h2>
          
          <div className="row">
            {impactVision.map((impact, index) => (
              <div key={index} className="col-lg-6 mb-4">
                <motion.div 
                  className="impact-vision-card"
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ 
                    scale: 1.03,
                    boxShadow: "0 25px 50px rgba(0,0,0,0.15)"
                  }}
                >
                  <div className="impact-header">
                    <div className="impact-icon">{impact.icon}</div>
                    <h4>{impact.category}</h4>
                  </div>
                  <div className="impact-metrics">
                    {impact.metrics.map((metric, idx) => (
                      <div key={idx} className="metric-item">
                        <span className="metric-bullet">•</span>
                        <span>{metric}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision Principles */}
      <section className="principles-section">
        <div className="container">
          <motion.h2 
            className="section-title text-center mb-5"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
          >
            Guiding Principles
          </motion.h2>
          
          <div className="row">
            {visionPrinciples.map((principle, index) => (
              <div key={index} className="col-lg-4 col-md-6 mb-4">
                <motion.div 
                  className="principle-card"
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ 
                    scale: 1.05,
                    rotateY: 5
                  }}
                >
                  <div className="principle-icon">{principle.icon}</div>
                  <h5>{principle.title}</h5>
                  <p>{principle.description}</p>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Future World Visualization */}
      <section className="future-world-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <motion.div 
                className="future-content"
                initial={{ x: -50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2>The World We're Building</h2>
                <p className="lead">
                  Imagine stepping off a plane in any city and immediately knowing where you belong, 
                  who your community is, and how to feel at home.
                </p>
                <div className="future-scenarios">
                  <div className="scenario">
                    <span className="scenario-icon">🌏</span>
                    <div>
                      <strong>Global Mobility</strong>
                      <p>Work from anywhere, belong everywhere. Your community travels with you.</p>
                    </div>
                  </div>
                  <div className="scenario">
                    <span className="scenario-icon">🤖</span>
                    <div>
                      <strong>AI-Powered Belonging</strong>
                      <p>Technology that understands human connection at the deepest level.</p>
                    </div>
                  </div>
                  <div className="scenario">
                    <span className="scenario-icon">🏙️</span>
                    <div>
                      <strong>Connected Cities</strong>
                      <p>Every city becomes part of a global network of communities.</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
            
            <div className="col-lg-6">
              <motion.div 
                className="future-visualization"
                initial={{ x: 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <div className="visualization-container">
                  <motion.div 
                    className="central-hub"
                    animate={{ 
                      rotate: 360,
                      scale: [1, 1.1, 1]
                    }}
                    transition={{ 
                      duration: 20, 
                      repeat: Infinity, 
                      ease: "linear" 
                    }}
                  >
                    <span>🌍</span>
                  </motion.div>
                  
                  {visionElements.map((element, index) => (
                    <motion.div
                      key={index}
                      className={`orbit-element orbit-${index + 1}`}
                      animate={{
                        rotate: 360
                      }}
                      transition={{
                        duration: 15 + index * 2,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                    >
                      <motion.div
                        className="orbit-content"
                        animate={{
                          rotate: -360
                        }}
                        transition={{
                          duration: 15 + index * 2,
                          repeat: Infinity,
                          ease: "linear"
                        }}
                      >
                        {element.icon}
                      </motion.div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      
    </div>
  );
};

export default Vision;